import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import type { User } from '../types';
import * as authApi from '../services/api/auth';
import * as storage from '../services/storage/storage';

interface Props {
  children: (ctx: { user: User; syncStatus: string; onLogout: () => Promise<void> }) => ReactNode;
}

export function AuthGate({ children }: Props) {
  const [user, setUser] = useState<User | null>(null);
  const [booting, setBooting] = useState(true);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState('lokal');

  useEffect(() => {
    let active = true;
    authApi.getMe()
      .then(async (result) => {
        if (!active || !result.user) return;
        storage.setActiveUser(result.user.id);
        setUser(result.user);
        await pullOrSeedProgress();
      })
      .catch(() => undefined)
      .finally(() => active && setBooting(false));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!user) return;
    let timer: number | null = null;
    let inFlight = false;
    let pending = false;

    const push = async () => {
      if (inFlight) {
        pending = true;
        return;
      }
      inFlight = true;
      setSyncStatus('synchronisiere…');
      try {
        await authApi.putProgress(storage.load());
        setSyncStatus('synchronisiert');
      } catch (err) {
        setSyncStatus(err instanceof Error ? `Sync-Fehler: ${err.message}` : 'Sync-Fehler');
      } finally {
        inFlight = false;
        if (pending) {
          pending = false;
          schedule();
        }
      }
    };

    const schedule = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(push, 1500);
    };

    window.addEventListener('storage:saved', schedule);
    return () => {
      window.removeEventListener('storage:saved', schedule);
      if (timer) window.clearTimeout(timer);
    };
  }, [user]);

  const title = useMemo(() => mode === 'login' ? 'Willkommen zurück' : 'Konto erstellen', [mode]);

  async function pullOrSeedProgress() {
    try {
      const remote = await authApi.getProgress();
      if (remote.data) storage.replaceCache(remote.data);
      else await authApi.putProgress(storage.load());
      setSyncStatus('synchronisiert');
    } catch {
      setSyncStatus('offline – lokale Daten aktiv');
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const normalizedEmail = email.trim();
    if (!normalizedEmail || !password) {
      setError('Bitte E-Mail und Passwort angeben.');
      return;
    }
    if (password.length < 10) {
      setError('Passwort muss mindestens 10 Zeichen haben.');
      return;
    }
    try {
      const result = mode === 'login'
        ? await authApi.login(normalizedEmail, password)
        : await authApi.register(normalizedEmail, password);
      storage.setActiveUser(result.user.id);
      setUser(result.user);
      await pullOrSeedProgress();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen.');
    }
  }

  async function onLogout() {
    try { await authApi.logout(); } catch { /* ignore */ }
    storage.setActiveUser(null);
    setUser(null);
    setPassword('');
    setSyncStatus('abgemeldet');
  }

  if (booting) {
    return <div className="boot-screen"><div className="spinner" />Lade Sitzung…</div>;
  }

  if (!user) {
    return (
      <div className="auth-overlay show">
        <div className="auth-card" role="dialog" aria-modal="true" aria-labelledby="authTitle">
          <div className="auth-brand">
            <div className="auth-brand-logo">📚</div>
            <div>
              <div className="auth-brand-title">Klausurphase</div>
              <div className="auth-brand-sub">React Smart Flashcards</div>
            </div>
          </div>
          <div className="auth-tabs" role="tablist">
            <button type="button" className={`auth-tab ${mode === 'login' ? 'active' : ''}`} onClick={() => setMode('login')}>Anmelden</button>
            <button type="button" className={`auth-tab ${mode === 'register' ? 'active' : ''}`} onClick={() => setMode('register')}>Registrieren</button>
          </div>
          <form onSubmit={submit} autoComplete="on" noValidate>
            <h2 id="authTitle" className="auth-title">{title}</h2>
            <label className="auth-label">E-Mail
              <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoComplete="email" />
            </label>
            <label className="auth-label">Passwort
              <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required minLength={10} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} />
              <span className="auth-hint">mind. 10 Zeichen</span>
            </label>
            {error && <div className="auth-error" role="alert">{error}</div>}
            <button type="submit" className="btn btn-primary auth-submit">{mode === 'login' ? 'Anmelden' : 'Registrieren'}</button>
            <p className="auth-note">Deine Lerndaten werden lokal gespeichert und nach Login verschlüsselt übertragen.</p>
          </form>
        </div>
      </div>
    );
  }

  return <>{children({ user, syncStatus, onLogout })}</>;
}
